import { ReactNode } from 'react';
import Media from 'react-media';

import { SWS_HOST } from '@/constants';

import { Grid, Heading, useTheme } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import DocumentHeader from '@components/DocumentHeader';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';

import { ImageHeader } from '@/components/Header';
import { NarrativeAuthor } from '@/components/NarrativeAuthor';

import { Narrative } from '@/pages/Narrative/hooks/useNarrative';

import { NarrativeTimestamp } from './NarrativeTimestamp';
import { NarrativeActions } from './NarrativeActions';

import {
  getCompanyUrlSegments,
  getNarrativeUrl,
} from '../../utils/getNarrativeUrl';
import { NarrativeCommunityBreadcrumbs } from '../NarrativeCommunityBreadcrumbs';

type Props = {
  narrative: Narrative;
  children?: ReactNode;
};

export const NarrativeHeader = ({ children, narrative }: Props) => {
  const theme = useTheme();
  const { isMobile } = useUserAgent();

  const {
    title,
    coverImageUrl,
    author,
    company,
    userHoldsPosition,
    isFeatured,
    excerpt,
  } = narrative;

  const coverImageSrc = useCloudflareImage({
    src: coverImageUrl,
    width: 720,
    height: 152,
    fit: 'cover',
  });

  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: coverImageUrl,
    breakpoints: [
      { width: 1024, height: 152 },
      { width: 1023, height: 135 },
      { width: 768, height: 135, fit: 'contain' },
      { width: 425, height: 135, fit: 'contain' },
      { width: 375, height: 135, fit: 'contain' },
    ],
  });

  const canonicalUrl = getNarrativeUrl({
    canonicalURL: company?.canonicalURL || '',
    narrativeSlug: narrative.slug,
  });

  const urlSegments = company?.canonicalURL
    ? getCompanyUrlSegments(company?.canonicalURL)
    : null;

  return (
    <Grid as="header" gap="16px" data-testid="narrative-header">
      <DocumentHeader
        title={`${company?.tickerSymbol} - ${title}`}
        titleSuffix=""
        allowCrawlers={isFeatured}
        openGraphImageUrl={coverImageUrl}
        description={excerpt}
      >
        <link rel="canonical" href={`${SWS_HOST}${canonicalUrl}`} />
      </DocumentHeader>
      <ImageHeader
        imageSource={coverImageSrc}
        imageSourceSet={coverImageSrcSet}
      >
        <Grid gap="16px">
          <NarrativeCommunityBreadcrumbs
            companyName={company?.name}
            companyNameSlug={urlSegments?.companyName}
            exchangeTicker={urlSegments?.exchangeTicker}
            market={urlSegments?.country}
            primaryIndustrySlug={urlSegments?.industry}
          />
          <Heading as="h1" typography="megaStrong">
            {title}
          </Heading>
          <Media
            queries={{ mobile: { maxWidth: theme.mdMaxRaw } }}
            defaultMatches={{ mobile: isMobile }}
          >
            {(matches) => {
              if (matches.mobile) {
                return (
                  <Grid
                    rowGap={`${theme.x2spacePx}px`}
                    templateRows="max-content max-content"
                  >
                    <Grid
                      rowGap={`${theme.x2spacePx}px`}
                      templateRows="max-content max-content"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <NarrativeAuthor
                        author={author}
                        userHoldsPosition={userHoldsPosition}
                        isAiNarrative={narrative.type === 'AI'}
                        priceTargetAnalystCount={
                          company?.analysisValue?.priceTargetAnalystCount
                        }
                      />
                      <NarrativeTimestamp narrative={narrative} />
                    </Grid>
                    <NarrativeActions narrative={narrative} />
                  </Grid>
                );
              }
              return (
                <Grid
                  columnGap={`${theme.x2spacePx}px`}
                  templateColumns="max-content max-content"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid
                    columnGap={`${theme.x2spacePx}px`}
                    templateColumns="max-content max-content"
                    justifyContent="space-between"
                    alignItems="end"
                  >
                    <NarrativeAuthor
                      author={author}
                      userHoldsPosition={userHoldsPosition}
                      isAiNarrative={narrative.type === 'AI'}
                      priceTargetAnalystCount={
                        company?.analysisValue?.priceTargetAnalystCount
                      }
                    />
                    <NarrativeTimestamp narrative={narrative} />
                  </Grid>
                  <NarrativeActions narrative={narrative} />
                </Grid>
              );
            }}
          </Media>
          {children}
        </Grid>
      </ImageHeader>
    </Grid>
  );
};
